import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ParseKeys, TFunction } from 'i18next';

import { getColumnHeaderText } from '@/utils/table/utils/columnHeaderText';

import type { AggregationType } from '../types';

type ColumnWithHeaderText<T> = TableProps.ColumnDefinition<T> & {
  headerText?: string;
};

const FIELD_AGGREGATION_LABEL_KEYS = {
  sum: 'dashboard.aggregationValueLabel.sum',
  mean: 'dashboard.aggregationValueLabel.mean',
  max: 'dashboard.aggregationValueLabel.max',
  min: 'dashboard.aggregationValueLabel.min',
} as const satisfies Record<
  Exclude<AggregationType, 'count'>,
  ParseKeys<'common'>
>;

export interface AggregationValueLabels {
  /**
   * Value-axis subtitle. A count measures entities ("No. of risks"); a field
   * aggregation measures the field itself ("Sum of Risk Number test").
   */
  axisLabel: string;
  /**
   * Series title for the single-series (no sub-category) tooltip. Undefined for
   * a count so the existing entity-plural title is preserved; set for field
   * aggregations so the hover label matches the value axis.
   */
  seriesTitle: string | undefined;
}

interface GetAggregationValueLabelsOptions<T> {
  t: TFunction;
  columnDefinitions: readonly ColumnWithHeaderText<T>[] | undefined;
  aggregationType: AggregationType | undefined;
  aggregationField: keyof T | undefined;
  entityNameSingular: ParseKeys<'common'>;
  entityNamePlural: ParseKeys<'common'>;
}

/**
 * Derives the value-axis subtitle and tooltip label for a chart widget from its
 * aggregation settings. For a field aggregation it mirrors the aggregation-aware
 * widget title produced by `universal-widget/settingsToTitle` (e.g. "Sum of Risk
 * Number test" — the title without the grouping) so the axis stops reading
 * "No. of {entity}" once an aggregation other than count is applied (CUS-106).
 */
export const getAggregationValueLabels = <T>({
  t,
  columnDefinitions,
  aggregationType,
  aggregationField,
  entityNameSingular,
  entityNamePlural,
}: GetAggregationValueLabelsOptions<T>): AggregationValueLabels => {
  if (!aggregationType || aggregationType === 'count' || !aggregationField) {
    return {
      axisLabel: t('dashboard.aggregationValueLabel.count', {
        entity: t(entityNamePlural),
      }),
      seriesTitle: undefined,
    };
  }

  const column = columnDefinitions?.find((c) => c.id === aggregationField);
  const fieldLabel =
    (column && getColumnHeaderText(column)) || String(aggregationField);
  // titleCase mirrors the auto-generated widget title (settingsToTitle) so the
  // axis reads as that title minus the trailing "by {category}".
  const label = t(FIELD_AGGREGATION_LABEL_KEYS[aggregationType], {
    entity: i18n.format(t(entityNameSingular), 'titleCase'),
    field: fieldLabel,
  });

  return { axisLabel: label, seriesTitle: label };
};
